import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import { projects } from '../data/projects';

const ProjectCard = ({ project }) => (
  <motion.div 
    layout
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9 }}
    whileHover={{ y: -10 }}
    className="group relative overflow-hidden rounded-2xl glass border border-slate-800 transition-all duration-500 hover:border-accent-primary/40 hover:shadow-[0_20px_40px_rgba(0,244,212,0.15)]"
  >
     {/* Image with hover overlay */}
     <div className="relative h-64 overflow-hidden">
        <img 
          src={project.image} 
          alt={project.title} 
          className="w-full h-full object-cover grayscale brightness-75 group-hover:grayscale-0 group-hover:brightness-100 group-hover:scale-110 transition-all duration-700"
        />
        <div className="absolute inset-0 bg-navy/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center gap-6">
           <a href={project.github} target="_blank" rel="noreferrer" className="p-4 bg-white/10 backdrop-blur-md rounded-full text-white text-2xl hover:bg-accent-primary hover:text-navy transition-all duration-300">
             <FaGithub />
           </a>
           <a href={project.demo} target="_blank" rel="noreferrer" className="p-4 bg-white/10 backdrop-blur-md rounded-full text-white text-2xl hover:bg-accent-primary hover:text-navy transition-all duration-300">
             <FaExternalLinkAlt />
           </a>
        </div>
     </div>

     {/* Content */}
     <div className="p-6 space-y-4">
        <div className="flex justify-between items-start">
           <h3 className="text-xl font-display font-bold text-white group-hover:text-accent-primary transition-colors">{project.title}</h3>
           <span className="text-[10px] font-mono uppercase tracking-widest text-accent-primary bg-accent-primary/10 px-2 py-1 rounded">{project.category}</span>
        </div>
        <p className="text-sm text-slate-400 line-clamp-2 leading-relaxed">
           {project.description}
        </p>
        <div className="flex flex-wrap gap-2 pt-2">
           {project.tech.map((t, i) => (
             <span key={i} className="text-[10px] font-mono text-slate-500 border border-slate-800 px-2 py-0.5 rounded group-hover:text-slate-300 transition-colors">{t}</span>
           ))}
        </div>
     </div>
  </motion.div>
);

const Projects = () => {
  const [filter, setFilter] = useState('All');
  const categories = ['All', 'Frontend', 'Backend', 'Full Stack'];

  const filteredProjects = filter === 'All' 
    ? projects 
    : projects.filter(p => p.category === filter);

  return (
    <div id="projects" className="w-full section-padding relative">
       <motion.div 
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="flex flex-col gap-10"
      >
        <div className="flex items-center gap-4">
          <span className="text-accent-primary font-mono text-xl">03.</span>
          <h2 className="text-3xl sm:text-4xl font-display font-bold">Featured Projects</h2>
          <div className="h-[1px] flex-grow bg-slate-800 ml-4 max-w-[300px]" />
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-4 mb-2">
           {categories.map((cat, idx) => (
             <button
               key={idx}
               onClick={() => setFilter(cat)}
               className={`font-mono text-xs tracking-widest uppercase pb-2 transition-all duration-300 border-b-2 ${filter === cat ? 'text-accent-primary border-accent-primary' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
             >
               {cat}
             </button>
           ))}
        </div>

        {/* Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Projects;
